import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, FolderPlus, Image as ImageIcon, Trash2, Download, Share2 } from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'

interface PhotoAlbumsWidgetProps {
  widget: any
  isExpanded: boolean
}

interface Album {
  id: number
  name: string
  description?: string
  cover_photo_url?: string
  is_shared: boolean
  created_at: string
  photo_count?: number
}

interface Photo {
  id: number
  album_id: number
  file_url: string
  thumbnail_url?: string
  caption?: string
  uploaded_at: string
}

export default function PhotoAlbumsWidget({ widget, isExpanded }: PhotoAlbumsWidgetProps) {
  const [selectedAlbum, setSelectedAlbum] = useState<Album | null>(null)
  const [showCreateAlbum, setShowCreateAlbum] = useState(false)
  const [newAlbumName, setNewAlbumName] = useState('')
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)
  const queryClient = useQueryClient()

  // Fetch albums
  const { data: albums, isLoading } = useQuery({
    queryKey: ['photo-albums'],
    queryFn: async () => {
      const { data } = await axios.get('/api/photos/albums')
      return data
    }
  })

  // Fetch photos for selected album
  const { data: photos } = useQuery({
    queryKey: ['album-photos', selectedAlbum?.id],
    queryFn: async () => {
      if (!selectedAlbum) return []
      const { data } = await axios.get(`/api/photos/albums/${selectedAlbum.id}`)
      return data.photos || []
    },
    enabled: !!selectedAlbum
  })

  // Create album mutation
  const createAlbumMutation = useMutation({
    mutationFn: async (name: string) => {
      const { data } = await axios.post('/api/photos/albums', { name })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['photo-albums'] })
      setShowCreateAlbum(false)
      setNewAlbumName('')
      toast.success('Album created!')
    }
  })

  // Upload photo mutation
  const uploadPhotoMutation = useMutation({
    mutationFn: async ({ albumId, file }: { albumId: number, file: File }) => {
      const formData = new FormData()
      formData.append('photo', file)
      const { data } = await axios.post(`/api/photos/albums/${albumId}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['album-photos'] })
      toast.success('Photo uploaded!')
    }
  })

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files || !selectedAlbum) return

    for (let i = 0; i < files.length; i++) {
      await uploadPhotoMutation.mutateAsync({
        albumId: selectedAlbum.id,
        file: files[i]
      })
    }
  }

  const handleCreateAlbum = () => {
    if (newAlbumName.trim()) {
      createAlbumMutation.mutate(newAlbumName)
    }
  }

  const renderCompactView = () => (
    <div>
      {isLoading ? (
        <div className="text-center py-8 text-gray-500">Loading albums...</div>
      ) : albums && albums.length > 0 ? (
        <div className="grid grid-cols-2 gap-3">
          {albums.slice(0, 4).map((album: Album) => (
            <button
              key={album.id}
              onClick={() => {
                setSelectedAlbum(album)
                if (isExpanded) {
                  // Already handled by expanded view
                }
              }}
              className="relative aspect-square rounded-lg overflow-hidden group"
            >
              {album.cover_photo_url ? (
                <img
                  src={album.cover_photo_url}
                  alt={album.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <ImageIcon size={32} className="text-gray-400" />
                </div>
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
              <div className="absolute bottom-2 left-2 right-2">
                <div className="font-medium text-white text-sm truncate">{album.name}</div>
                <div className="text-xs text-white/80">
                  {album.photo_count || 0} photos
                </div>
              </div>
            </button>
          ))}
        </div>
      ) : (
        <div className="text-center py-8">
          <ImageIcon size={48} className="text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-4">No albums yet</p>
          <button
            onClick={() => setShowCreateAlbum(true)}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition text-sm"
          >
            Create Album
          </button>
        </div>
      )}
    </div>
  )

  const renderExpandedView = () => (
    <div className="h-full flex gap-4">
      {/* Albums Sidebar */}
      <div className="w-64 overflow-y-auto border-r border-gray-200 pr-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-gray-900">Albums</h3>
          <button
            onClick={() => setShowCreateAlbum(true)}
            className="p-2 hover:bg-gray-100 rounded-lg transition"
            title="Create album"
          >
            <FolderPlus size={20} />
          </button>
        </div>

        {showCreateAlbum && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg">
            <input
              type="text"
              value={newAlbumName}
              onChange={(e) => setNewAlbumName(e.target.value)}
              placeholder="Album name"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded mb-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              autoFocus
            />
            <div className="flex gap-2">
              <button
                onClick={handleCreateAlbum}
                disabled={!newAlbumName.trim()}
                className="flex-1 px-3 py-1 bg-primary-600 text-white text-sm rounded hover:bg-primary-700 transition disabled:opacity-50"
              >
                Create
              </button>
              <button
                onClick={() => {
                  setShowCreateAlbum(false)
                  setNewAlbumName('')
                }}
                className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300 transition"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          {albums?.map((album: Album) => (
            <button
              key={album.id}
              onClick={() => setSelectedAlbum(album)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition text-left ${
                selectedAlbum?.id === album.id
                  ? 'bg-primary-50 border-2 border-primary-500'
                  : 'hover:bg-gray-50 border-2 border-transparent'
              }`}
            >
              <div className="w-12 h-12 rounded overflow-hidden flex-shrink-0">
                {album.cover_photo_url ? (
                  <img src={album.cover_photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                    <ImageIcon size={20} className="text-gray-400" />
                  </div>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 text-sm truncate">{album.name}</div>
                <div className="text-xs text-gray-600">
                  {album.photo_count || 0} photos
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Photos Grid */}
      <div className="flex-1 overflow-y-auto">
        {selectedAlbum ? (
          <div>
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedAlbum.name}</h2>
                {selectedAlbum.description && (
                  <p className="text-sm text-gray-600">{selectedAlbum.description}</p>
                )}
              </div>
              <div className="flex gap-2">
                <button className="p-2 hover:bg-gray-100 rounded-lg transition" title="Share album">
                  <Share2 size={20} />
                </button>
                <label className="p-2 hover:bg-gray-100 rounded-lg transition cursor-pointer" title="Upload photos">
                  <Upload size={20} />
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>
            </div>

            {photos && photos.length > 0 ? (
              <div className="grid grid-cols-4 gap-3">
                {photos.map((photo: Photo) => (
                  <button
                    key={photo.id}
                    onClick={() => setSelectedPhoto(photo)}
                    className="relative aspect-square rounded-lg overflow-hidden group"
                  >
                    <img
                      src={photo.thumbnail_url || photo.file_url}
                      alt={photo.caption || ''}
                      className="w-full h-full object-cover group-hover:scale-110 transition duration-300"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition" />
                  </button>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <ImageIcon size={64} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No photos in this album</p>
                <label className="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition cursor-pointer">
                  <Upload size={20} />
                  Upload Photos
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            Select an album to view photos
          </div>
        )}
      </div>
    </div>
  )

  // Photo viewer modal
  {selectedPhoto && (
    <div
      className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
      onClick={() => setSelectedPhoto(null)}
    >
      <button
        onClick={() => setSelectedPhoto(null)}
        className="absolute top-4 right-4 p-2 bg-white/10 hover:bg-white/20 rounded-full transition"
      >
        ✕
      </button>
      <img
        src={selectedPhoto.file_url}
        alt={selectedPhoto.caption || ''}
        className="max-w-full max-h-full object-contain"
        onClick={(e) => e.stopPropagation()}
      />
      {selectedPhoto.caption && (
        <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-6 py-3 rounded-lg">
          {selectedPhoto.caption}
        </div>
      )}
    </div>
  )}

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 overflow-hidden">
        {isExpanded ? renderExpandedView() : renderCompactView()}
      </div>
    </div>
  )
}




